import React from "react";

interface ReactLoadingProps {
  type?: "blank" | "balls" | "bars" | "bubbles" | "cubes" | "cylon" | "spin" | "spinningBubbles" | "spokes";
  color?: string;
  height?: number | string;
  width?: number | string;
  delay?: number;
  className?: string;
}

/**
 * Drop-in replacement for the unmaintained `react-loading` package.
 * Renders a simple CSS spinner matching the most common usage patterns.
 */
const ReactLoading: React.FC<ReactLoadingProps> = ({
  type = "spin",
  color = "#9da3a8",
  height = 64,
  width = 64,
  delay = 0,
  className = "",
}) => {
  const size = typeof width === "string" ? width : `${width}px`;
  const h = typeof height === "string" ? height : `${height}px`;

  const spinnerStyle: React.CSSProperties = {
    width: size,
    height: h,
    display: "inline-block",
    animationDelay: delay ? `${delay}ms` : undefined,
  };

  if (type === "bubbles") {
    return (
      <svg
        className={className}
        style={spinnerStyle}
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
        fill={color}
      >
        <circle cx="4" cy="16" r="3">
          <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
        </circle>
        <circle cx="16" cy="16" r="3">
          <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0.33s" />
        </circle>
        <circle cx="28" cy="16" r="3">
          <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0.66s" />
        </circle>
      </svg>
    );
  }

  if (type === "spokes") {
    return (
      <svg
        className={className}
        style={spinnerStyle}
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
        fill={color}
      >
        <g>
          <animateTransform attributeName="transform" type="rotate" values="0 16 16;360 16 16" dur="0.8s" repeatCount="indefinite" />
          {[...Array(8)].map((_, i) => {
            const angle = i * 45;
            const opacity = 1 - i * 0.1;
            return (
              <rect
                key={i}
                x="15"
                y="2"
                width="2"
                height="6"
                rx="1"
                opacity={opacity}
                transform={`rotate(${angle} 16 16)`}
              />
            );
          })}
        </g>
      </svg>
    );
  }

  // Default: "spin" type and all others
  return (
    <svg
      className={className}
      style={{
        ...spinnerStyle,
        animation: "react-loading-spin 0.8s linear infinite",
        animationDelay: delay ? `${delay}ms` : undefined,
      }}
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <circle
        cx="16"
        cy="16"
        r="14"
        stroke={color}
        strokeWidth="3"
        strokeDasharray="60 28"
        strokeLinecap="round"
      />
      <style>{`
        @keyframes react-loading-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </svg>
  );
};

export default ReactLoading;
